import { useState, useEffect, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { cx } from '@emotion/css';
import { Button, Tooltip, Drawer, Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { OnOrder, subscriptionStatus } from 'services/api/pay';
import { useAuthorization } from 'components/Authorization';
import { useQuery } from 'components/RouterQuery';
import { useMemoizedFn } from 'ahooks';

import PackageDlg from './PackageDlg';
import ZhixiaGift from './ZhixiaGift';
import { isQuickChat } from 'productLine';
import { useTransAppProductName } from 'hooks/useTrans';
import { PackageCard } from './PackageCard';
import { OpenTrafficOrderModalButton } from './OrderAI/Button';
import { match } from 'ts-pattern';
import { useComboInfo, useIsEnterprisePkg, useOrderedPackagesActions } from '../hooks/useComboInfo';
import { SHOW_ADDON_PACKAGE_ORDER_ENTRY } from '../constants';
import { filter } from 'lodash-es';
import AIServiceOverview, { AILightWeightServiceOverview } from './AIServiceOverview';
import ManageSubscriptionModal from './ManageSubscriptionModal';
import { CatalogErrorRetry } from './CatalogErrorRetry';
import { usePriceZones, usePricingCatalog } from '../hooks/usePricing';
import { catalogToPackageInfoV2, disabledUpgradeTierIds, packageIdLabel } from '../catalogAdapter';
import { narrowPriceZone, zonePlanName, PriceZoneCode } from '../priceZone';
import { logPricing } from '../pricingLogger';

export interface OrderDealProps {
  onOrder: OnOrder;
}

function OrderedDeal(props: OrderDealProps) {
  const { orderType: defaultOrderType, lightWeightSource = '' } = useQuery();
  const { onOrder } = props;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [orderType, setOrderType] = useState<Pay.OrderType>();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { orgId } = payload || {};

  // 升级沿用当前套餐价区，旧套餐回退 CN；续订采用服务端默认方案，可在弹窗内更换。
  const {
    zones,
    defaultZone,
    defaultCurrency,
    defaultCurrencySource,
    isLoading: loadingPriceZones,
  } = usePriceZones();
  const [renewZoneOverride, setRenewZoneOverride] = useState<PriceZoneCode>();

  const [upgradeSubscriptionCurrency, setUpgradeSubscriptionCurrency] =
    useState<General.Currency>();
  const [loadingSubscriptionCurrency, setLoadingSubscriptionCurrency] = useState(false);
  // 升级锁定币种：后端按上次购买币种校验升级，变更币种报价/下单会 400「升级不支持变更币种」。
  // 生效订阅币种优先，非订阅/查询失败时回退 price-zones 的 LAST_PURCHASE 币种
  const lastPurchaseCurrency =
    defaultCurrencySource === 'LAST_PURCHASE'
      ? (defaultCurrency as General.Currency | undefined)
      : undefined;
  const orderedPackages = useComboInfo(true);
  const workingPackage = orderedPackages.find((v) => v.status === 1);
  const workingZone = narrowPriceZone(workingPackage?.priceZone);
  const dlgZone = orderType === 'UPGRADE' ? workingZone ?? 'CN' : renewZoneOverride ?? defaultZone;
  const isEnterprisePkg = useIsEnterprisePkg();
  const [manageSubscriptionVisible, setManageSubscriptionVisible] = useState(false);

  const {
    catalog: upgradeCatalog,
    isLoading: loadingUpgradeCatalog,
    isError: upgradeCatalogError,
    refetch: refetchUpgradeCatalog,
  } = usePricingCatalog(dlgZone, 'UPGRADE', {
    enabled: isModalVisible && orderType === 'UPGRADE',
  });
  const {
    catalog: renewCatalog,
    isLoading: loadingRenewCatalog,
    isError: renewCatalogError,
    refetch: refetchRenewCatalog,
  } = usePricingCatalog(dlgZone, 'RENEW', {
    enabled: isModalVisible && orderType === 'RENEW',
  });
  // 升级只升不降：全部档位展示，不高于当前档位的置灰禁选（口径同 perseus upgradeCandidates）
  const disabledUpgradeIds = useMemo(
    () => disabledUpgradeTierIds(upgradeCatalog, workingPackage),
    [upgradeCatalog, workingPackage],
  );
  const upgradePackageInfo = useMemo(
    () => catalogToPackageInfoV2(upgradeCatalog, t),
    [upgradeCatalog, t],
  );
  const renewPackageInfo = useMemo(
    () => catalogToPackageInfoV2(renewCatalog, t),
    [renewCatalog, t],
  );

  const { canUpgrade, canRenew } = useOrderedPackagesActions();

  // 生效中的订阅套餐
  const workingSubscription = orderedPackages.find(
    (v) => (v.status === 1 || v.status === 0) && v.autoRenewing,
  );
  const activeSubscription = orderedPackages.find((v) => v.status === 1 && v.autoRenewing);
  // 升级等待历史购买币种，续订等待默认价区，均需价区接口就绪。
  const upgradeLoading = loadingUpgradeCatalog || loadingSubscriptionCurrency || loadingPriceZones;
  const loadingRenew = loadingRenewCatalog || loadingPriceZones;

  // 触发升级套餐服务（目录数据已按价区就绪，直接开抽屉）
  const triggerUpgradePackage = useMemoizedFn(() => {
    logPricing('upgrade_clicked', {
      orgId,
      currentPackageId: workingPackage?.id,
      currentRegionPackageId: workingPackage?.regionPackageId,
      currentPriceZone: workingPackage?.priceZone,
      displayedPackageName: packageIdLabel(
        workingPackage?.id,
        t,
        workingPackage?.comboName,
        workingPackage?.regionPackageId,
      ),
      selectedPriceZone: workingZone ?? 'CN',
      legacyZoneFallback: !workingZone,
      lastPurchaseCurrency,
      autoRenewing: activeSubscription?.autoRenewing ?? false,
    });
    setLoadingSubscriptionCurrency(!!activeSubscription);
    setRenewZoneOverride(undefined);
    setOrderType('UPGRADE');
    setIsModalVisible(true);
    if (activeSubscription) {
      subscriptionStatus()
        .then((subscription) => {
          logPricing('subscription_currency_loaded', { orgId, currency: subscription?.currency });
          setUpgradeSubscriptionCurrency(subscription?.currency);
        })
        .catch((error) => {
          logPricing('subscription_currency_failed', { orgId, lastPurchaseCurrency }, error);
        })
        .finally(() => setLoadingSubscriptionCurrency(false));
    } else {
      setUpgradeSubscriptionCurrency(undefined);
    }
  });
  // 触发续订套餐服务
  const triggerRenewPackage = useMemoizedFn(() => {
    logPricing('renew_clicked', {
      orgId,
      currentPackageId: workingPackage?.id,
      currentRegionPackageId: workingPackage?.regionPackageId,
      currentPriceZone: workingPackage?.priceZone,
      displayedPackageName: packageIdLabel(
        workingPackage?.id,
        t,
        workingPackage?.comboName,
        workingPackage?.regionPackageId,
      ),
      selectedPriceZone: defaultZone,
      lastPurchaseCurrency,
    });
    setRenewZoneOverride(undefined);
    setOrderType('RENEW');
    setIsModalVisible(true);
  });

  useEffect(() => {
    // 根据路由query orderType 决定是否触发套餐变更
    const defOrderType = defaultOrderType?.toUpperCase();
    if (canUpgrade && defOrderType === 'UPGRADE') {
      triggerUpgradePackage();
    }
    if (canRenew && defOrderType === 'RENEW') {
      triggerRenewPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrderType, canUpgrade, canRenew]);

  const titleMap: Record<Pay.TrafficPackageType, string> = {
    INCREASE_THE_MONTHLY_CAP: t('aiorder_type1_title'),
    TRAFFIC_PACKAGE: t('aiorder_type2_title'),
    ENTERPRISE_TRAFFIC_PACKAGE: t('aiorder_type3_title'),
  };

  const trafficPackageCardNode = (card = false) => {
    const trafficPkgs = filter(orderedPackages, (v) => v.productName === '流量包');
    if (trafficPkgs.length > 0 || true) {
      const cards = trafficPkgs.map((v) => {
        return (
          <PackageCard
            key={`${v.productName}-${v.validTimeEnd}`}
            title={titleMap[v.trafficPackageType!]}
            orderedPackage={v}
            color={v.trafficPackageType === 'INCREASE_THE_MONTHLY_CAP' ? 'purple' : 'green'}
            showHandler
          />
        );
      });
      return card ? (
        cards
      ) : (
        <div
          className={cx(
            'mt-4 rounded border border-solid border-fill-200',
            isQuickChat ? '!mt-0' : '',
          )}
        >
          <ClientBar>
            <span>{t('header.AI参与服务买家数')}</span>
            {SHOW_ADDON_PACKAGE_ORDER_ENTRY && (
              <div>
                <OpenTrafficOrderModalButton />
              </div>
            )}
          </ClientBar>
          <div className="px-[16px] pt-[12px]">
            <AIServiceOverview simple />
          </div>
          <PackageDeal>{cards}</PackageDeal>
        </div>
      );
    }
  };

  return orderedPackages ? (
    <div>
      <ZhixiaGift />
      {orderedPackages.find((v) => v.productName === '乐聊客服工作台') ? (
        <div
          className={cx('rounded border border-solid border-fill-200', isQuickChat ? '!mt-0' : '')}
        >
          <ClientBar>
            {/* 新价格体系：套餐信息只留价区+档位名，productName 不再作区块标题 */}
            <span>{t('套餐')}</span>
            <ClientBarBtns>
              {workingSubscription && (
                <Button onClick={() => setManageSubscriptionVisible(true)}>
                  {t('管理自动续费')}
                </Button>
              )}
              <Tooltip title={!canUpgrade ? t('market.升级套餐提示') : ''}>
                <Button
                  disabled={upgradeLoading || !canUpgrade}
                  onClick={triggerUpgradePackage}
                  className="!mr-4"
                >
                  {t('market.升级套餐')} {upgradeLoading ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
              <Tooltip title={!canRenew ? t('market.续订套餐提示') : ''}>
                <Button
                  disabled={loadingRenew || !canRenew}
                  onClick={triggerRenewPackage}
                  className="!mr-0"
                >
                  {t('market.续订套餐')} {loadingRenew ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
            </ClientBarBtns>
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              const cardZone = narrowPriceZone(v.priceZone);
              return v.productName === '乐聊客服工作台' ? (
                <PackageCard
                  key={v.id}
                  // 新旧套餐统一档位名主标题（旧套餐走 套餐.* 档位词条）、
                  // 价区副标题（旧套餐无快照回退跨境方案），不再展示 productName
                  title={packageIdLabel(v.id, t, v.comboName, v.regionPackageId)}
                  subTitle={zonePlanName(cardZone ?? 'CN', t)}
                  orderedPackage={v}
                  color="orange"
                />
              ) : null;
            })}
          </PackageDeal>
        </div>
      ) : null}

      {orderedPackages.find((v) => v.productName === 'QikChat工作台') ? (
        <div
          className={cx(
            'mt-4 rounded border border-solid border-fill-200',
            isQuickChat ? '!mt-0' : '',
          )}
        >
          <ClientBar>
            <span>{appProductNameT('quickchat_workbench')}</span>
            <ClientBarBtns>
              {workingSubscription && (
                <Button onClick={() => setManageSubscriptionVisible(true)}>
                  {t('管理自动续费')}
                </Button>
              )}
              <Tooltip title={!canUpgrade ? t('market.升级套餐提示') : ''}>
                <Button
                  disabled={upgradeLoading || !canUpgrade}
                  onClick={triggerUpgradePackage}
                  className="!mr-4"
                >
                  {t('market.升级套餐')} {upgradeLoading ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
              <Tooltip title={!canRenew ? t('market.续订套餐提示') : ''}>
                <Button
                  disabled={loadingRenew || !canRenew}
                  onClick={triggerRenewPackage}
                  className="!mr-0"
                >
                  {t('market.续订套餐')} {loadingRenew ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
            </ClientBarBtns>
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              const cardZone = narrowPriceZone(v.priceZone);
              return v.productName === 'QikChat工作台' ? (
                <PackageCard
                  key={v.id}
                  // 与乐聊区块同口径：档位名主标题 + 价区副标题（旧套餐回退跨境方案）
                  title={packageIdLabel(v.id, t, v.comboName, v.regionPackageId)}
                  subTitle={zonePlanName(cardZone ?? 'CN', t)}
                  orderedPackage={v}
                  color="orange"
                />
              ) : null;
            })}
          </PackageDeal>
        </div>
      ) : null}

      {orderedPackages.find((v) => v.productName === '乐聊高级客服') ? (
        <div
          className={cx('rounded border border-solid border-fill-200', isQuickChat ? '!mt-0' : '')}
        >
          <ClientBar>
            <span>
              {t('packageType.2')}
              <Tooltip placement="top" title={t('market.乐聊高级客服提示')}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              return match(v)
                .with({ productName: '乐聊高级客服' }, () => (
                  <PackageCard
                    key={v.id}
                    title={t('packageName.高级智能客服机器人')}
                    orderedPackage={v}
                    color="blue"
                    showHandler
                  />
                ))
                .otherwise(() => null);
            })}
            {trafficPackageCardNode(true)}
          </PackageDeal>
        </div>
      ) : null}

      {!isEnterprisePkg && (
        <AILightWeightServiceOverview
          source={`serviceInfo${lightWeightSource ? '-' + lightWeightSource : ''}`}
        />
      )}
      {!isEnterprisePkg && trafficPackageCardNode()}

      <ManageSubscriptionModal
        visible={manageSubscriptionVisible}
        orderedPackage={workingSubscription}
        onCancel={() => setManageSubscriptionVisible(false)}
      />
      <DrawerModal
        title={orderType === 'UPGRADE' ? t('market.升级套餐') : t('market.续订套餐')}
        visible={isModalVisible}
        width={890}
        footer={null}
        destroyOnClose
        // onOk={handleOk}
        onClose={() => {
          setIsModalVisible(false);
          setRenewZoneOverride(undefined);
        }}
      >
        {orderType && dlgZone && orgId ? (
          orderType === 'UPGRADE' ? (
            upgradeLoading || !upgradePackageInfo ? (
              <div className="flex justify-center py-[80px]">
                {upgradeCatalogError ? (
                  <CatalogErrorRetry onRetry={() => refetchUpgradeCatalog()} />
                ) : (
                  <Spin />
                )}
              </div>
            ) : (
              <PackageDlg
                key={`UPGRADE-${dlgZone}`}
                orderType="UPGRADE"
                orgId={orgId}
                orderedPackages={orderedPackages}
                packageInfo={upgradePackageInfo}
                zone={dlgZone}
                // 升级锁原价区（不提供更换入口）
                zones={[dlgZone]}
                lockedCurrency={upgradeSubscriptionCurrency ?? lastPurchaseCurrency}
                // 升级只升不降：不高于当前档位置灰禁选
                disabledUpgradeIds={disabledUpgradeIds}
                onOrder={onOrder}
                onSuccess={() => setIsModalVisible(false)}
              />
            )
          ) : loadingRenew || !renewPackageInfo ? (
            <div className="flex justify-center py-[80px]">
              {renewCatalogError ? (
                <CatalogErrorRetry onRetry={() => refetchRenewCatalog()} />
              ) : (
                <Spin />
              )}
            </div>
          ) : (
            <PackageDlg
              key={`RENEW-${dlgZone}`}
              orderType="RENEW"
              orgId={orgId}
              orderedPackages={orderedPackages}
              packageInfo={renewPackageInfo}
              zone={dlgZone}
              // 续订可更换价区
              zones={zones}
              // 续费币种候选需并入已支付货币（升级不支持变更币种，续费可以）
              lastPurchaseCurrency={lastPurchaseCurrency}
              onZoneChange={setRenewZoneOverride}
              onOrder={onOrder}
              onSuccess={() => setIsModalVisible(false)}
            />
          )
        ) : null}
      </DrawerModal>
    </div>
  ) : null;
}

export default OrderedDeal;

const ClientBar = styled.div`
  height: 48px;
  background: var(--fett-erp-card-bg);
  display: flex;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  & > span {
    color: var(--fett-text-primary);
    span.anticon {
      color: var(--fett-text-secondary);
      margin-left: 4px;
    }
  }
`;
const ClientBarBtns = styled.div`
  button:first-of-type {
    margin-right: 16px;
  }
`;
const PackageDeal = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
  padding-bottom: 0;
  flex-wrap: wrap;
  > div:not(:last-child) {
    margin-right: 18px;
  }
`;
const DrawerModal = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 78% !important;
    min-width: 800px;
  }
`;
